/*
 * Hand-drawn inline SVG avatars for Neo (fox 🦊) and Lyria (butterfly 🦋).
 *
 * Inline so they ship with the bundle (no extra HTTP calls), scale crisply
 * on any retina display, and can be tinted / animated via CSS. The
 * `talking` prop adds a gentle bob + glow so the kid sees who's speaking.
 */

function NeoFox({ size = 96, talking = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="img"
      aria-label="Neo le petit renard"
      style={{
        filter: talking
          ? "drop-shadow(0 0 14px rgba(255, 138, 101, 0.6))"
          : "drop-shadow(0 4px 10px rgba(0,0,0,0.08))",
        animation: talking ? "neoBob 1.4s ease-in-out infinite" : "none",
      }}
    >
      {/* halo */}
      <circle cx="60" cy="62" r="50" fill="#fdf3e8" />

      {/* ears */}
      <path d="M28 36 L40 12 L48 38 Z" fill="#ff8a65" />
      <path d="M92 36 L80 12 L72 38 Z" fill="#ff8a65" />
      <path d="M32 32 L40 18 L45 34 Z" fill="#3b2a4a" opacity="0.18" />
      <path d="M88 32 L80 18 L75 34 Z" fill="#3b2a4a" opacity="0.18" />

      {/* head */}
      <ellipse cx="60" cy="68" rx="38" ry="34" fill="#ffb38a" />
      <ellipse cx="60" cy="60" rx="34" ry="28" fill="#ff8a65" />

      {/* cheek + chin (lighter belly) */}
      <ellipse cx="60" cy="86" rx="22" ry="14" fill="#fff3e8" />

      {/* eyes */}
      <circle cx="46" cy="58" r="6" fill="#3b2a4a" />
      <circle cx="74" cy="58" r="6" fill="#3b2a4a" />
      <circle cx="48" cy="56" r="2" fill="#ffffff" />
      <circle cx="76" cy="56" r="2" fill="#ffffff" />

      {/* nose */}
      <path d="M56 70 Q60 76 64 70 Q60 72 56 70 Z" fill="#3b2a4a" />
      {/* smile */}
      <path d="M50 78 Q60 86 70 78" stroke="#3b2a4a" strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* sparkle */}
      <g opacity={talking ? 1 : 0.6}>
        <circle cx="98" cy="28" r="2" fill="#ff7043" />
        <circle cx="22" cy="44" r="1.6" fill="#ffd29a" />
        <circle cx="104" cy="68" r="1.6" fill="#ffd29a" />
      </g>

      <style>{`
        @keyframes neoBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
      `}</style>
    </svg>
  );
}

function LyriaButterfly({ size = 96, talking = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="img"
      aria-label="Lyria le papillon"
      style={{
        filter: talking
          ? "drop-shadow(0 0 14px rgba(196, 182, 255, 0.7))"
          : "drop-shadow(0 4px 10px rgba(0,0,0,0.08))",
        animation: talking ? "lyriaFlutter 1.1s ease-in-out infinite" : "none",
      }}
    >
      {/* halo */}
      <circle cx="60" cy="60" r="50" fill="#f3e8ff" />

      {/* upper wings */}
      <g style={{ transformOrigin: "60px 60px", animation: talking ? "wingsTop 1s ease-in-out infinite" : "none" }}>
        <path d="M60 60 Q22 22 16 50 Q12 66 30 70 Q48 74 60 60 Z" fill="#c8b6ff" />
        <path d="M60 60 Q98 22 104 50 Q108 66 90 70 Q72 74 60 60 Z" fill="#c8b6ff" />
        {/* wing accents */}
        <circle cx="34" cy="46" r="5" fill="#ffd29a" />
        <circle cx="86" cy="46" r="5" fill="#ffd29a" />
        <circle cx="26" cy="58" r="3" fill="#ff8a65" />
        <circle cx="94" cy="58" r="3" fill="#ff8a65" />
      </g>

      {/* lower wings */}
      <g style={{ transformOrigin: "60px 60px", animation: talking ? "wingsBot 1s ease-in-out infinite" : "none" }}>
        <path d="M60 64 Q34 102 24 86 Q18 74 38 72 Q52 70 60 64 Z" fill="#a98aff" opacity="0.85" />
        <path d="M60 64 Q86 102 96 86 Q102 74 82 72 Q68 70 60 64 Z" fill="#a98aff" opacity="0.85" />
      </g>

      {/* body */}
      <ellipse cx="60" cy="62" rx="5" ry="22" fill="#3b2a4a" />
      <circle cx="60" cy="44" r="6" fill="#3b2a4a" />
      {/* antennae */}
      <path d="M57 40 Q50 28 46 30" stroke="#3b2a4a" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M63 40 Q70 28 74 30" stroke="#3b2a4a" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <circle cx="46" cy="30" r="2" fill="#ff8a65" />
      <circle cx="74" cy="30" r="2" fill="#ff8a65" />
      {/* eyes */}
      <circle cx="58" cy="43" r="1.4" fill="#fff" />
      <circle cx="62" cy="43" r="1.4" fill="#fff" />

      <style>{`
        @keyframes lyriaFlutter {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes wingsTop {
          0%, 100% { transform: scaleX(1); }
          50%      { transform: scaleX(0.85); }
        }
        @keyframes wingsBot {
          0%, 100% { transform: scaleX(1); }
          50%      { transform: scaleX(0.9); }
        }
      `}</style>
    </svg>
  );
}

export default function Mascot({ character = "neo", size = 96, talking = false }) {
  if (character === "lyria") return <LyriaButterfly size={size} talking={talking} />;
  return <NeoFox size={size} talking={talking} />;
}
